import React, { useCallback, useEffect, useState } from 'react';
import { Type, AlignLeft, AlignJustify, Minus, Plus, X } from 'lucide-react';
import { useDarkMode } from '../contexts/DarkModeContext';
import AnchoredPopover from './AnchoredPopover';

// Typography settings interface
export interface TypographyConfig {
  fontFamily: 'system' | 'inter' | 'merriweather' | 'serif' | 'sans' | 'mono';
  fontSize: number; // 12-24px
  lineHeight: number; // 1.2-2.4
  textAlign: 'left' | 'justify';
  maxWidth: 'narrow' | 'medium' | 'wide' | 'full';
}

// Default settings
export const defaultTypographyConfig: TypographyConfig = {
  fontFamily: 'inter',
  fontSize: 16,
  lineHeight: 1.7,
  textAlign: 'left',
  maxWidth: 'wide',
};

// Font family mappings
export const fontFamilies: Record<string, string> = {
  system: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  inter: 'Inter, "Helvetica Neue", Helvetica, Arial, sans-serif',
  merriweather: '"Merriweather", Georgia, Cambria, "Times New Roman", Times, serif',
  serif: '"Merriweather", Georgia, Cambria, "Times New Roman", Times, serif',
  sans: 'Inter, "Helvetica Neue", Helvetica, Arial, sans-serif',
  mono: '"SF Mono", "Fira Code", Consolas, Monaco, monospace'
};

// Max width mappings
export const maxWidths: Record<string, string> = {
  narrow: '580px',
  medium: '720px',
  wide: '900px',
  full: '100%'
};

// LocalStorage key
const STORAGE_KEY = 'rescript-typography-settings';

// Load settings from localStorage
export const loadTypographySettings = (): TypographyConfig => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return { ...defaultTypographyConfig, ...JSON.parse(stored) };
    }
  } catch (e) {
    console.warn('[Typography] Failed to load settings:', e);
  }
  return defaultTypographyConfig;
};

// Save settings to localStorage
export const saveTypographySettings = (config: TypographyConfig) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
  } catch (e) {
    console.warn('[Typography] Failed to save settings:', e);
  }
};

interface TypographySettingsProps {
  config: TypographyConfig;
  onChange: (config: TypographyConfig) => void;
  isOpen: boolean;
  onClose: () => void;
  anchorRef?: React.RefObject<HTMLElement | null>;
}

const TypographySettings: React.FC<TypographySettingsProps> = ({
  config,
  onChange,
  isOpen,
  onClose,
  anchorRef
}) => {
  const { isDarkMode } = useDarkMode();
  const [fontSizeDraft, setFontSizeDraft] = useState(config.fontSize);
  const [lineHeightDraft, setLineHeightDraft] = useState(config.lineHeight);

  useEffect(() => {
    setFontSizeDraft(config.fontSize);
  }, [config.fontSize]);

  useEffect(() => {
    setLineHeightDraft(config.lineHeight);
  }, [config.lineHeight]);

  const updateConfig = useCallback((updates: Partial<TypographyConfig>) => {
    const newConfig = { ...config, ...updates };
    onChange(newConfig);
    saveTypographySettings(newConfig);
  }, [config, onChange]);

  const commitFontSize = useCallback((value = fontSizeDraft) => {
    if (value !== config.fontSize) {
      updateConfig({ fontSize: value });
    }
  }, [config.fontSize, fontSizeDraft, updateConfig]);

  const commitLineHeight = useCallback((value = lineHeightDraft) => {
    if (value !== config.lineHeight) {
      updateConfig({ lineHeight: value });
    }
  }, [config.lineHeight, lineHeightDraft, updateConfig]);

  if (!isOpen) return null;

  const fontOptions: { value: TypographyConfig['fontFamily']; label: string; preview: string }[] = [
    { value: 'system', label: 'System', preview: 'Aa' },
    { value: 'inter', label: 'Inter', preview: 'Aa' },
    { value: 'merriweather', label: 'Merri', preview: 'Aa' },
    { value: 'mono', label: 'Mono', preview: 'Aa' }
  ];

  const widthOptions: { value: TypographyConfig['maxWidth']; label: string }[] = [
    { value: 'narrow', label: 'Narrow' },
    { value: 'medium', label: 'Medium' },
    { value: 'wide', label: 'Wide' },
    { value: 'full', label: 'Full' }
  ];

  return (
    <AnchoredPopover
      isOpen={isOpen}
      onClose={onClose}
      anchorRef={anchorRef || { current: null }}
      align="end"
      offset={8}
      className={`w-80 rounded-xl shadow-2xl border overflow-hidden ${
        isDarkMode
          ? 'bg-gray-800 border-gray-700'
          : 'bg-white border-gray-200'
      }`}
    >
      {/* Header */}
      <div className={`flex items-center justify-between px-4 py-3 border-b ${
        isDarkMode ? 'border-gray-700' : 'border-gray-100'
      }`}>
        <div className="flex items-center gap-2">
          <Type size={18} className={isDarkMode ? 'text-gray-300' : 'text-gray-600'} />
          <span className={`font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
            Reading Settings
          </span>
        </div>
        <button
          onClick={onClose}
          className={`p-1 rounded-lg transition-colors ${
            isDarkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'
          }`}
        >
          <X size={16} />
        </button>
      </div>

      <div className="p-4 space-y-5">
        {/* Font Family */}
        <div>
          <label className={`block text-xs font-medium mb-2 uppercase tracking-wide ${
            isDarkMode ? 'text-gray-400' : 'text-gray-500'
          }`}>
            Font
          </label>
          <div className="grid grid-cols-4 gap-2">
            {fontOptions.map((font) => (
              <button
                key={font.value}
                onClick={() => updateConfig({ fontFamily: font.value })}
                className={`flex flex-col items-center py-2 px-1 rounded-lg border transition-all ${
                  config.fontFamily === font.value
                    ? isDarkMode
                      ? 'border-blue-500 bg-blue-500/10 text-blue-400'
                      : 'border-blue-500 bg-blue-50 text-blue-600'
                    : isDarkMode
                      ? 'border-gray-700 hover:border-gray-600 text-gray-400'
                      : 'border-gray-200 hover:border-gray-300 text-gray-600'
                }`}
              >
                <span
                  className="text-lg font-medium mb-0.5"
                  style={{ fontFamily: fontFamilies[font.value] }}
                >
                  {font.preview}
                </span>
                <span className="text-[10px]">{font.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Font Size */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className={`text-xs font-medium uppercase tracking-wide ${
              isDarkMode ? 'text-gray-400' : 'text-gray-500'
            }`}>
              Size
            </label>
            <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              {fontSizeDraft}px
            </span>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => {
                const nextValue = Math.max(12, fontSizeDraft - 1);
                setFontSizeDraft(nextValue);
                commitFontSize(nextValue);
              }}
              disabled={fontSizeDraft <= 12}
              className={`p-2 rounded-lg transition-colors ${
                fontSizeDraft <= 12
                  ? 'opacity-40 cursor-not-allowed'
                  : isDarkMode
                    ? 'hover:bg-gray-700 text-gray-400'
                    : 'hover:bg-gray-100 text-gray-600'
              }`}
            >
              <Minus size={16} />
            </button>
            <div className="flex-1 relative">
              <input
                type="range"
                min={12}
                max={24}
                value={fontSizeDraft}
                onChange={(e) => setFontSizeDraft(parseInt(e.target.value, 10))}
                onMouseUp={() => commitFontSize()}
                onTouchEnd={() => commitFontSize()}
                onKeyUp={() => commitFontSize()}
                onBlur={() => commitFontSize()}
                className="w-full h-2 rounded-full appearance-none cursor-pointer accent-blue-500"
                style={{
                  background: isDarkMode
                    ? `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${((fontSizeDraft - 12) / 12) * 100}%, #374151 ${((fontSizeDraft - 12) / 12) * 100}%, #374151 100%)`
                    : `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${((fontSizeDraft - 12) / 12) * 100}%, #e5e7eb ${((fontSizeDraft - 12) / 12) * 100}%, #e5e7eb 100%)`
                }}
              />
            </div>
            <button
              onClick={() => {
                const nextValue = Math.min(24, fontSizeDraft + 1);
                setFontSizeDraft(nextValue);
                commitFontSize(nextValue);
              }}
              disabled={fontSizeDraft >= 24}
              className={`p-2 rounded-lg transition-colors ${
                fontSizeDraft >= 24
                  ? 'opacity-40 cursor-not-allowed'
                  : isDarkMode
                    ? 'hover:bg-gray-700 text-gray-400'
                    : 'hover:bg-gray-100 text-gray-600'
              }`}
            >
              <Plus size={16} />
            </button>
          </div>
        </div>

        {/* Line Height */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className={`text-xs font-medium uppercase tracking-wide ${
              isDarkMode ? 'text-gray-400' : 'text-gray-500'
            }`}>
              Line Spacing
            </label>
            <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              {lineHeightDraft.toFixed(1)}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => {
                const nextValue = Math.max(1.2, +(lineHeightDraft - 0.1).toFixed(1));
                setLineHeightDraft(nextValue);
                commitLineHeight(nextValue);
              }}
              disabled={lineHeightDraft <= 1.2}
              className={`p-2 rounded-lg transition-colors ${
                lineHeightDraft <= 1.2
                  ? 'opacity-40 cursor-not-allowed'
                  : isDarkMode
                    ? 'hover:bg-gray-700 text-gray-400'
                    : 'hover:bg-gray-100 text-gray-600'
              }`}
            >
              <Minus size={16} />
            </button>
            <div className="flex-1 relative">
              <input
                type="range"
                min={1.2}
                max={2.4}
                step={0.1}
                value={lineHeightDraft}
                onChange={(e) => setLineHeightDraft(parseFloat(e.target.value))}
                onMouseUp={() => commitLineHeight()}
                onTouchEnd={() => commitLineHeight()}
                onKeyUp={() => commitLineHeight()}
                onBlur={() => commitLineHeight()}
                className="w-full h-2 rounded-full appearance-none cursor-pointer accent-blue-500"
                style={{
                  background: isDarkMode
                    ? `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${((lineHeightDraft - 1.2) / 1.2) * 100}%, #374151 ${((lineHeightDraft - 1.2) / 1.2) * 100}%, #374151 100%)`
                    : `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${((lineHeightDraft - 1.2) / 1.2) * 100}%, #e5e7eb ${((lineHeightDraft - 1.2) / 1.2) * 100}%, #e5e7eb 100%)`
                }}
              />
            </div>
            <button
              onClick={() => {
                const nextValue = Math.min(2.4, +(lineHeightDraft + 0.1).toFixed(1));
                setLineHeightDraft(nextValue);
                commitLineHeight(nextValue);
              }}
              disabled={lineHeightDraft >= 2.4}
              className={`p-2 rounded-lg transition-colors ${
                lineHeightDraft >= 2.4
                  ? 'opacity-40 cursor-not-allowed'
                  : isDarkMode
                    ? 'hover:bg-gray-700 text-gray-400'
                    : 'hover:bg-gray-100 text-gray-600'
              }`}
            >
              <Plus size={16} />
            </button>
          </div>
        </div>

        {/* Text Width */}
        <div>
          <label className={`block text-xs font-medium mb-2 uppercase tracking-wide ${
            isDarkMode ? 'text-gray-400' : 'text-gray-500'
          }`}>
            Width
          </label>
          <div className="grid grid-cols-4 gap-2">
            {widthOptions.map((width) => (
              <button
                key={width.value}
                onClick={() => updateConfig({ maxWidth: width.value })}
                className={`py-2 px-1 rounded-lg border text-xs transition-all ${
                  config.maxWidth === width.value
                    ? isDarkMode
                      ? 'border-blue-500 bg-blue-500/10 text-blue-400'
                      : 'border-blue-500 bg-blue-50 text-blue-600'
                    : isDarkMode
                      ? 'border-gray-700 hover:border-gray-600 text-gray-400'
                      : 'border-gray-200 hover:border-gray-300 text-gray-600'
                }`}
              >
                {width.label}
              </button>
            ))}
          </div>
        </div>

        {/* Text Alignment */}
        <div>
          <label className={`block text-xs font-medium mb-2 uppercase tracking-wide ${
            isDarkMode ? 'text-gray-400' : 'text-gray-500'
          }`}>
            Alignment
          </label>
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => updateConfig({ textAlign: 'left' })}
              className={`flex items-center justify-center gap-2 py-2 rounded-lg border transition-all ${
                config.textAlign === 'left'
                  ? isDarkMode
                    ? 'border-blue-500 bg-blue-500/10 text-blue-400'
                    : 'border-blue-500 bg-blue-50 text-blue-600'
                  : isDarkMode
                    ? 'border-gray-700 hover:border-gray-600 text-gray-400'
                    : 'border-gray-200 hover:border-gray-300 text-gray-600'
              }`}
            >
              <AlignLeft size={16} />
              <span className="text-xs">Left</span>
            </button>
            <button
              onClick={() => updateConfig({ textAlign: 'justify' })}
              className={`flex items-center justify-center gap-2 py-2 rounded-lg border transition-all ${
                config.textAlign === 'justify'
                  ? isDarkMode
                    ? 'border-blue-500 bg-blue-500/10 text-blue-400'
                    : 'border-blue-500 bg-blue-50 text-blue-600'
                  : isDarkMode
                    ? 'border-gray-700 hover:border-gray-600 text-gray-400'
                    : 'border-gray-200 hover:border-gray-300 text-gray-600'
              }`}
            >
              <AlignJustify size={16} />
              <span className="text-xs">Justify</span>
            </button>
          </div>
        </div>

        {/* Reset Button */}
        <button
          onClick={() => {
            onChange(defaultTypographyConfig);
            saveTypographySettings(defaultTypographyConfig);
          }}
          className={`w-full py-2 text-xs rounded-lg transition-colors ${
            isDarkMode
              ? 'text-gray-400 hover:bg-gray-700'
              : 'text-gray-500 hover:bg-gray-100'
          }`}
        >
          Reset to defaults
        </button>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        input[type="range"]::-webkit-slider-thumb {
          -webkit-appearance: none;
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background: #3b82f6;
          cursor: pointer;
          border: 2px solid white;
          box-shadow: 0 1px 3px rgba(0,0,0,0.2);
        }
        input[type="range"]::-moz-range-thumb {
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background: #3b82f6;
          cursor: pointer;
          border: 2px solid white;
          box-shadow: 0 1px 3px rgba(0,0,0,0.2);
        }
      `}</style>
    </AnchoredPopover>
  );
};

export default TypographySettings;
